import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Save, Rocket, Eye, Sparkles, Loader2, ChevronDown, ChevronRight, ChevronUp, ShieldCheck, AlertTriangle, Info, QrCode, Image as ImageIcon, Upload, Star, Flame, Leaf, Crown, BadgePercent, Video, FileText, FileDown } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { api } from "../api";
import { useToast } from "../hooks/use-toast";

const uid = () => Math.random().toString(36).slice(2);

const BADGE_OPTIONS = [
  { code: "consigliato", label: "Consigliato", Icon: Sparkles },
  { code: "specialita", label: "Specialità della casa", Icon: Crown },
  { code: "best_seller", label: "Best seller", Icon: Star },
  { code: "novita", label: "Novità", Icon: Sparkles },
  { code: "promo", label: "Promo", Icon: BadgePercent },
  { code: "piccante", label: "Piccante", Icon: Flame },
  { code: "vegetariano", label: "Vegetariano", Icon: Leaf },
];
const badgeLabel = (c) => BADGE_OPTIONS.find((b) => b.code === c)?.label || c;

export default function MenuEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const { toast } = useToast();
  const [m, setM] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState({});
  const [dishEdit, setDishEdit] = useState(null); // {catId, dish}
  const [suggesting, setSuggesting] = useState(false);
  const [needsInfo, setNeedsInfo] = useState([]);
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const [mn, cat] = await Promise.all([api.menuGet(id), api.allergenCatalog()]);
      setM(mn.data); setCatalog(cat.data);
      const o = {}; (mn.data.categories || []).forEach((c) => (o[c.id] = true)); setOpen(o);
    })();
  }, [id]);

  if (!m) return <p className="text-muted-foreground">Caricamento…</p>;

  const label = (code) => catalog.find((a) => a.code === code)?.label || code;
  const setCats = (cats) => setM((x) => ({ ...x, categories: cats }));
  const addCat = () => setCats([...(m.categories || []), { id: uid(), name: "Nuova categoria", description: "", dishes: [] }]);
  const updCat = (cid, k, v) => setCats(m.categories.map((c) => c.id === cid ? { ...c, [k]: v } : c));
  const delCat = (cid) => setCats(m.categories.filter((c) => c.id !== cid));
  const addDish = (cid) => {
    const d = { id: uid(), name: "", description: "", price: "", ingredients: "", variants: [], allergens_document: [], allergens_suggested: [], allergens_confirmed: [], allergen_status: "da_verificare", available: true, media: [], main: null, badges: [], recommended: false, note: "", pairing: "", suggested: [] };
    setCats(m.categories.map((c) => c.id === cid ? { ...c, dishes: [...c.dishes, d] } : c));
    setDishEdit({ catId: cid, dish: d });
  };

  const saveDish = (catId, dish) => {
    setCats(m.categories.map((c) => c.id === catId ? { ...c, dishes: c.dishes.map((d) => d.id === dish.id ? dish : d) } : c));
  };
  const delDish = (catId, did) => setCats(m.categories.map((c) => c.id === catId ? { ...c, dishes: c.dishes.filter((d) => d.id !== did) } : c));
  const moveDish = (catId, idx, dir) => {
    setCats(m.categories.map((c) => {
      if (c.id !== catId) return c;
      const ds = [...c.dishes]; const j = idx + dir;
      if (j < 0 || j >= ds.length) return c;
      [ds[idx], ds[j]] = [ds[j], ds[idx]];
      return { ...c, dishes: ds };
    }));
  };

  // flat list of dishes for the "suggested pairing" picker
  const allDishes = (m.categories || []).flatMap((c) => c.dishes.map((d) => ({ id: d.id, name: d.name || "(senza nome)" })));

  const persist = async (publish) => {
    setSaving(true);
    try {
      const payload = { name: m.name, description: m.description, language: m.language, cover: m.cover || "", logo: m.logo || "", info: m.info || "", pdf_public: !!m.pdf_public, categories: m.categories };
      if (publish !== undefined) payload.status = publish ? "published" : "draft";
      const { data } = await api.menuUpdate(id, payload);
      setM(data);
      toast({ title: publish ? "Menù pubblicato" : "Salvato" });
    } catch (e) { toast({ title: "Errore", description: e?.response?.data?.detail, variant: "destructive" }); }
    finally { setSaving(false); }
  };

  const pdf = async (download) => {
    setPdfBusy(true);
    try {
      await persist(); // PDF is always generated from the current, saved data
      const { data } = await api.menuPdf(id, download);
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      if (download) { const a = document.createElement("a"); a.href = url; a.download = `${(m.name || "menu").replace(/[^\w\- ]+/g, "")}.pdf`; a.click(); }
      else window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast({ title: "PDF non generato", description: e?.response?.data?.detail || "Riprova", variant: "destructive" }); }
    finally { setPdfBusy(false); }
  };

  const suggestAllergens = async () => {
    if (!dishEdit) return;
    setSuggesting(true); setNeedsInfo([]);
    try {
      const { data } = await api.aiAllergens({ dish_name: dishEdit.dish.name, ingredients: dishEdit.dish.ingredients, description: dishEdit.dish.description });
      setDishEdit((p) => ({ ...p, dish: { ...p.dish, allergens_suggested: data.suggested || [] } }));
      setNeedsInfo(data.needs_info || []);
      toast({ title: "Suggerimenti pronti", description: "Controlla e conferma gli allergeni." });
    } catch (e) {
      toast({ title: "AI non disponibile", description: "Inserisci gli allergeni manualmente.", variant: "destructive" });
    } finally { setSuggesting(false); }
  };

  const importedBanner = sp.get("imported") === "1";

  const generateQr = async () => {
    try {
      await api.qrCreate({ name: `QR — ${m.name}`, dynamic: true, target_type: "menu", target_value: id, style: { fg: "#123C46", bg: "#ffffff" } });
      toast({ title: "QR generato", description: "Trovi il QR di questo menù nella sezione QR Code." });
    } catch (e) { toast({ title: "Errore", description: e?.response?.data?.detail, variant: "destructive" }); }
  };

  const dishPhoto = (d) => { const p = (d.media || []).filter((x) => x.kind === "image"); const main = p.find((x) => x.id === d.main) || p[0]; return main ? api.mediaUrl(main.id, 160) : null; };
  const dishHasVideo = (d) => (d.media || []).some((x) => x.kind === "video") || !!d.external_video;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <button onClick={() => navigate("/app/menu")} className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Menù</button>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={generateQr} className="gap-2" data-testid="menu-generate-qr"><QrCode className="h-4 w-4" /> Genera QR</Button>
          <Button variant="outline" onClick={() => pdf(false)} disabled={pdfBusy || saving} className="gap-2" data-testid="menu-pdf-preview">{pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Anteprima PDF</Button>
          <Button variant="outline" onClick={() => pdf(true)} disabled={pdfBusy || saving} className="gap-2" data-testid="menu-pdf-download"><FileDown className="h-4 w-4" /> Scarica PDF</Button>
          <Button variant="outline" onClick={() => persist()} disabled={saving} className="gap-2" data-testid="menu-save-draft"><Save className="h-4 w-4" /> Salva bozza</Button>
          {m.status === "published" && <Button variant="outline" onClick={() => window.open(`/m/${id}`, "_blank")} className="gap-2" data-testid="menu-view"><Eye className="h-4 w-4" /> Vedi</Button>}
          <Button className="btn-grad text-white font-semibold gap-2" onClick={() => persist(true)} disabled={saving} data-testid="menu-publish"><Rocket className="h-4 w-4" /> Pubblica</Button>
        </div>
      </div>

      {importedBanner && (
        <div className="rounded-xl bg-accent/15 border border-accent/30 px-4 py-3 mb-5 flex items-start gap-2 text-sm">
          <Info className="h-4 w-4 text-primary mt-0.5 shrink-0" />
          <span>Menù importato dall'AI. <b>Controlla ogni voce</b> (prezzi, descrizioni) e conferma gli allergeni prima di pubblicare. I dati estratti sono proposte da verificare.</span>
        </div>
      )}

      {/* Menu header settings */}
      <div className="rounded-2xl border border-border bg-white p-5 card-shadow mb-5 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="text-xs text-muted-foreground">Nome del menù / locale</Label><Input value={m.name} onChange={(e) => setM({ ...m, name: e.target.value })} className="h-11 mt-1 font-semibold text-lg" data-testid="menu-name-input" /></div>
          <div><Label className="text-xs text-muted-foreground">Info rapide (indirizzo, orari, telefono)</Label><Input value={m.info || ""} onChange={(e) => setM({ ...m, info: e.target.value })} className="h-11 mt-1" placeholder="Via Roma 12 · 12–15 / 19–23 · 051 123456" data-testid="menu-info-input" /></div>
        </div>
        <div><Label className="text-xs text-muted-foreground">Descrizione</Label><Textarea value={m.description || ""} onChange={(e) => setM({ ...m, description: e.target.value })} className="mt-1 min-h-[52px]" placeholder="Cucina italiana tradizionale, prodotti freschi di stagione." data-testid="menu-description-input" /></div>
        <div className="grid grid-cols-2 gap-4">
          <HeaderImage label="Copertina" testid="menu-cover" value={m.cover} aspect="aspect-[16/9]" onChange={(v) => setM({ ...m, cover: v })} />
          <HeaderImage label="Logo" testid="menu-logo" value={m.logo} aspect="aspect-square" onChange={(v) => setM({ ...m, logo: v })} />
        </div>
        <label className="flex items-center justify-between rounded-xl border border-border bg-muted/20 px-4 py-3 gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium flex items-center gap-1.5"><FileDown className="h-4 w-4 text-primary" /> Permetti download PDF pubblico</p>
            <p className="text-xs text-muted-foreground mt-0.5">Mostra il pulsante «Scarica menù PDF» nel menù pubblico. Il PDF è sempre generato dai dati correnti.</p>
          </div>
          <Switch checked={!!m.pdf_public} onCheckedChange={(v) => setM({ ...m, pdf_public: v })} data-testid="menu-pdf-public" />
        </label>
      </div>

      <div className="space-y-4">
        {(m.categories || []).map((c) => (
          <div key={c.id} className="rounded-2xl border border-border bg-white card-shadow overflow-hidden">
            <div className="flex items-center gap-2 p-4 bg-muted/30">
              <button onClick={() => setOpen((o) => ({ ...o, [c.id]: !o[c.id] }))}>{open[c.id] ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}</button>
              <Input value={c.name} onChange={(e) => updCat(c.id, "name", e.target.value)} className="h-9 font-semibold border-transparent bg-transparent focus-visible:border-input px-1" />
              <Badge variant="secondary" className="text-xs shrink-0">{c.dishes.length} piatti</Badge>
              <button onClick={() => delCat(c.id)} className="text-muted-foreground hover:text-destructive ml-1"><Trash2 className="h-4 w-4" /></button>
            </div>
            {open[c.id] && (
              <div className="p-4 space-y-2">
                {c.dishes.map((d, idx) => {
                  const photo = dishPhoto(d);
                  return (
                    <div key={d.id} className="rounded-xl border border-border p-2.5 hover:bg-muted/40 flex items-center gap-3" data-testid={`editor-dish-${d.id}`}>
                      <div className="flex flex-col">
                        <button onClick={() => moveDish(c.id, idx, -1)} disabled={idx === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-30" title="Su"><ChevronUp className="h-4 w-4" /></button>
                        <button onClick={() => moveDish(c.id, idx, 1)} disabled={idx === c.dishes.length - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-30" title="Giù"><ChevronDown className="h-4 w-4" /></button>
                      </div>
                      <button onClick={() => setDishEdit({ catId: c.id, dish: d })} className="flex-1 min-w-0 flex items-center gap-3 text-left">
                        {photo ? <img src={photo} alt="" className="h-12 w-12 rounded-lg object-cover shrink-0" /> : <span className="h-12 w-12 rounded-lg bg-muted grid place-items-center shrink-0 text-muted-foreground"><ImageIcon className="h-5 w-5" /></span>}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium truncate">{d.name || "(senza nome)"}</span>
                            {d.recommended && <Sparkles className="h-3.5 w-3.5 text-amber-500 shrink-0" title="Consigliato" />}
                            {dishHasVideo(d) && <Video className="h-3.5 w-3.5 text-primary shrink-0" title="Video" />}
                          </div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {(d.badges || []).slice(0, 3).map((b) => <Badge key={b} variant="outline" className="text-[10px] py-0">{badgeLabel(b)}</Badge>)}
                            {d.allergen_status === "confermato" && <Badge className="bg-emerald-50 text-emerald-600 text-[10px] gap-1 py-0"><ShieldCheck className="h-3 w-3" /> Allergeni</Badge>}
                            {d.allergen_status === "da_aggiornare" && <Badge variant="outline" className="text-amber-600 text-[10px] py-0">Riverifica</Badge>}
                            {d.allergen_status === "da_verificare" && <Badge variant="outline" className="text-muted-foreground text-[10px] py-0">Da verificare</Badge>}
                          </div>
                        </div>
                        <span className="text-sm font-semibold shrink-0">{d.price}</span>
                      </button>
                    </div>
                  );
                })}
                <Button variant="outline" size="sm" className="gap-1.5 mt-1" onClick={() => addDish(c.id)} data-testid={`add-dish-${c.id}`}><Plus className="h-4 w-4" /> Aggiungi piatto</Button>
              </div>
            )}
          </div>
        ))}
        <Button variant="outline" className="gap-2" onClick={addCat} data-testid="add-category"><Plus className="h-4 w-4" /> Aggiungi categoria</Button>
      </div>

      {/* Dish editor */}
      <Dialog open={!!dishEdit} onOpenChange={(o) => { if (!o) { saveDish(dishEdit.catId, dishEdit.dish); setDishEdit(null); setNeedsInfo([]); } }}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Modifica piatto</DialogTitle></DialogHeader>
          {dishEdit && (
            <DishForm
              dish={dishEdit.dish}
              catalog={catalog}
              label={label}
              needsInfo={needsInfo}
              suggesting={suggesting}
              allDishes={allDishes.filter((x) => x.id !== dishEdit.dish.id)}
              onSuggest={suggestAllergens}
              onChange={(d) => setDishEdit({ ...dishEdit, dish: d })}
            />
          )}
          <DialogFooter>
            <Button variant="outline" className="text-destructive" onClick={() => { delDish(dishEdit.catId, dishEdit.dish.id); setDishEdit(null); }}>Elimina piatto</Button>
            <Button className="btn-grad text-white font-semibold" onClick={() => { saveDish(dishEdit.catId, dishEdit.dish); setDishEdit(null); setNeedsInfo([]); }} data-testid="dish-done">Fatto</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HeaderImage({ label, value, aspect, onChange, testid }) {
  const { toast } = useToast();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const url = value ? (/^https?:\/\//.test(value) ? value : api.mediaUrl(value)) : null;
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.uploadMedia(fd); onChange(data.id); }
    catch (e) { toast({ title: "Caricamento non riuscito", description: e?.response?.data?.detail || "Riprova", variant: "destructive" }); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label} <span className="opacity-60">(opzionale)</span></Label>
      <div className={`mt-1 relative ${aspect} rounded-xl border-2 border-dashed border-border overflow-hidden bg-muted/30`}>
        {url ? (
          <>
            <img src={url} alt="" className="h-full w-full object-cover" />
            <button onClick={() => onChange("")} className="absolute top-1.5 right-1.5 bg-black/60 text-white rounded-full p-1" data-testid={`${testid}-remove`}><Trash2 className="h-3.5 w-3.5" /></button>
          </>
        ) : (
          <button onClick={() => ref.current?.click()} className="absolute inset-0 grid place-items-center text-muted-foreground hover:text-primary" data-testid={`${testid}-upload`}>
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <div className="flex flex-col items-center gap-1"><Upload className="h-5 w-5" /><span className="text-[11px]">Carica</span></div>}
          </button>
        )}
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} />
      </div>
    </div>
  );
}

function DishForm({ dish, catalog, label, needsInfo, suggesting, allDishes, onSuggest, onChange }) {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const photoRef = useRef(null);
  const videoRef = useRef(null);
  const media = dish.media || [];
  const photos = media.filter((x) => x.kind === "image");
  const video = media.find((x) => x.kind === "video");

  const uploadFiles = async (files, kind) => {
    setUploading(true);
    try {
      const added = [];
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.uploadMedia(fd, (ev) => { if (ev.total) setProgress(Math.round(ev.loaded / ev.total * 100)); });
        added.push({ id: data.id, kind: data.kind });
      }
      setProgress(0);
      let nm = [...media];
      if (kind === "video") nm = nm.filter((x) => x.kind !== "video");
      nm = [...nm, ...added];
      const main = dish.main || (kind === "image" && photos.length === 0 ? added[0]?.id : dish.main);
      onChange({ ...dish, media: nm, main });
      toast({ title: kind === "video" ? "Video caricato" : "Foto caricate" });
    } catch (e) {
      toast({ title: "Caricamento non riuscito", description: e?.response?.data?.detail || "Riprova", variant: "destructive" });
    } finally { setUploading(false); }
  };

  const removeMedia = async (mid) => {
    try { await api.deleteMedia(mid); } catch {}
    const nm = media.filter((x) => x.id !== mid);
    onChange({ ...dish, media: nm, main: dish.main === mid ? (nm.find((x) => x.kind === "image")?.id || null) : dish.main });
  };

  const set = (k, v) => {
    const d = { ...dish, [k]: v };
    if (k === "ingredients" && dish.allergen_status === "confermato") d.allergen_status = "da_aggiornare";
    onChange(d);
  };
  const toggleBadge = (code) => {
    const has = (dish.badges || []).includes(code);
    onChange({ ...dish, badges: has ? dish.badges.filter((b) => b !== code) : [...(dish.badges || []), code] });
  };
  const toggleSuggested = (did) => {
    const has = (dish.suggested || []).includes(did);
    onChange({ ...dish, suggested: has ? dish.suggested.filter((x) => x !== did) : [...(dish.suggested || []), did] });
  };
  const toggleConfirmed = (code) => {
    const has = dish.allergens_confirmed.includes(code);
    const next = has ? dish.allergens_confirmed.filter((c) => c !== code) : [...dish.allergens_confirmed, code];
    onChange({ ...dish, allergens_confirmed: next });
  };
  const confirmAll = () => onChange({ ...dish, allergen_status: "confermato", confirmed_at: new Date().toISOString() });

  const suggested = dish.allergens_suggested || [];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-[1fr_120px] gap-3">
        <div><Label className="text-xs text-muted-foreground">Nome</Label><Input value={dish.name} onChange={(e) => set("name", e.target.value)} className="h-10 mt-1" data-testid="dish-name" /></div>
        <div><Label className="text-xs text-muted-foreground">Prezzo</Label><Input value={dish.price} onChange={(e) => set("price", e.target.value)} className="h-10 mt-1" placeholder="es. 9,00 €" data-testid="dish-price" /></div>
      </div>
      <div><Label className="text-xs text-muted-foreground">Descrizione</Label><Textarea value={dish.description} onChange={(e) => set("description", e.target.value)} className="mt-1 min-h-[52px]" /></div>
      <div><Label className="text-xs text-muted-foreground">Ingredienti (separati da virgola)</Label><Textarea value={dish.ingredients} onChange={(e) => set("ingredients", e.target.value)} className="mt-1 min-h-[52px]" placeholder="pasta, vongole, aglio..." /></div>

      {/* Highlight / badges */}
      <div className="rounded-xl border border-border p-4 bg-muted/20">
        <Label className="text-sm font-semibold flex items-center gap-1.5 mb-2"><Sparkles className="h-4 w-4 text-primary" /> Evidenza & Badge</Label>
        <label className="flex items-center justify-between rounded-lg bg-white border border-border px-3 py-2 mb-3">
          <span className="text-sm">Metti tra i consigliati <span className="text-muted-foreground">(sezione "Da provare")</span></span>
          <Switch checked={!!dish.recommended} onCheckedChange={(v) => set("recommended", v)} data-testid="dish-recommended" />
        </label>
        <div className="flex flex-wrap gap-2">
          {BADGE_OPTIONS.map((b) => {
            const on = (dish.badges || []).includes(b.code);
            return (
              <button key={b.code} onClick={() => toggleBadge(b.code)} data-testid={`dish-badge-${b.code}`}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${on ? "bg-primary text-white border-primary" : "bg-white text-foreground/70 border-border hover:border-primary/40"}`}>
                <b.Icon className="h-3.5 w-3.5" /> {b.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Media: photos & video */}
      <div className="rounded-xl border border-border p-4 bg-muted/20">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-sm font-semibold flex items-center gap-1.5"><ImageIcon className="h-4 w-4 text-primary" /> Foto & Video</Label>
          {uploading && <div className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin text-primary" />{progress > 0 && <span className="text-xs text-muted-foreground">{progress}%</span>}</div>}
        </div>
        <div className="grid grid-cols-4 gap-2">
          {photos.map((p) => (
            <div key={p.id} className="relative group aspect-square rounded-lg overflow-hidden border border-border">
              <img src={api.mediaUrl(p.id, 160)} alt="" className="h-full w-full object-cover" />
              {dish.main === p.id && <span className="absolute top-1 left-1 bg-primary text-white text-[9px] px-1.5 py-0.5 rounded">Principale</span>}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                {dish.main !== p.id && <button onClick={() => onChange({ ...dish, main: p.id })} className="text-white text-[10px] bg-white/20 rounded px-1.5 py-0.5">Principale</button>}
                <button onClick={() => removeMedia(p.id)} className="text-white bg-white/20 rounded p-1"><Trash2 className="h-3 w-3" /></button>
              </div>
            </div>
          ))}
          <button onClick={() => photoRef.current?.click()} className="aspect-square rounded-lg border-2 border-dashed border-border grid place-items-center text-muted-foreground hover:border-primary/50 hover:bg-white" data-testid="dish-add-photo">
            <Plus className="h-5 w-5" />
          </button>
          <input ref={photoRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadFiles([...e.target.files], "image")} />
        </div>
        <p className="text-[11px] text-muted-foreground mt-1.5">Immagini JPG/PNG/WebP, max 8 MB. La foto principale appare nell'elenco del menù.</p>

        <div className="mt-3 border-t border-border pt-3">
          <Label className="text-xs text-muted-foreground">Video del piatto</Label>
          {video ? (
            <div className="mt-1.5 flex items-center gap-2">
              <video src={api.mediaUrl(video.id)} className="h-16 w-24 rounded-lg object-cover bg-black" muted />
              <span className="text-xs text-muted-foreground flex-1">Video caricato</span>
              <button onClick={() => removeMedia(video.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
            </div>
          ) : (
            <div className="mt-1.5 space-y-2">
              <Button type="button" size="sm" variant="outline" className="gap-1.5 w-full" onClick={() => videoRef.current?.click()} disabled={uploading} data-testid="dish-add-video">
                <Upload className="h-3.5 w-3.5" /> Carica video (MP4/MOV, max 60 MB)
              </Button>
              <input ref={videoRef} type="file" accept="video/*" className="hidden" onChange={(e) => uploadFiles([...e.target.files], "video")} />
              <Input value={dish.external_video || ""} onChange={(e) => set("external_video", e.target.value)} className="h-9" placeholder="oppure incolla link YouTube / Shorts" />
            </div>
          )}
        </div>
      </div>

      {/* Variants / supplements */}
      <div className="rounded-xl border border-border p-4 bg-muted/20">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-sm font-semibold">Varianti / Supplementi</Label>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => onChange({ ...dish, variants: [...(dish.variants || []), { name: "", price: "" }] })}><Plus className="h-3.5 w-3.5" /> Aggiungi</Button>
        </div>
        {(dish.variants || []).length === 0 ? <p className="text-xs text-muted-foreground">Es. porzione piccola/grande, senza lattosio (+1€)…</p> : (
          <div className="space-y-2">
            {(dish.variants || []).map((v, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input value={v.name} onChange={(e) => { const nv = [...dish.variants]; nv[i] = { ...v, name: e.target.value }; onChange({ ...dish, variants: nv }); }} className="h-9 flex-1" placeholder="Nome variante" />
                <Input value={v.price} onChange={(e) => { const nv = [...dish.variants]; nv[i] = { ...v, price: e.target.value }; onChange({ ...dish, variants: nv }); }} className="h-9 w-24" placeholder="Prezzo" />
                <button onClick={() => onChange({ ...dish, variants: dish.variants.filter((_, j) => j !== i) })} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Note & pairing & suggested */}
      <div className="rounded-xl border border-border p-4 bg-muted/20 space-y-3">
        <div><Label className="text-xs text-muted-foreground">Nota del gestore (facoltativa)</Label><Textarea value={dish.note || ""} onChange={(e) => set("note", e.target.value)} className="mt-1 min-h-[44px]" placeholder="Es. Burrata fresca consegnata ogni mattina." data-testid="dish-note" /></div>
        <div><Label className="text-xs text-muted-foreground">Abbinamento consigliato (facoltativo)</Label><Input value={dish.pairing || ""} onChange={(e) => set("pairing", e.target.value)} className="h-9 mt-1" placeholder="Es. Vermentino di Sardegna" data-testid="dish-pairing" /></div>
        {allDishes.length > 0 && (
          <div>
            <Label className="text-xs text-muted-foreground">Prodotti suggeriti ("Ti potrebbe piacere")</Label>
            <div className="mt-1 max-h-32 overflow-y-auto rounded-lg border border-border bg-white divide-y divide-border">
              {allDishes.map((x) => (
                <label key={x.id} className="flex items-center gap-2 px-2.5 py-1.5 text-sm cursor-pointer hover:bg-muted/40">
                  <Checkbox checked={(dish.suggested || []).includes(x.id)} onCheckedChange={() => toggleSuggested(x.id)} />
                  <span className="truncate">{x.name}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Allergen workflow */}
      <div className="rounded-xl border border-border p-4 bg-muted/20">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-sm font-semibold flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-primary" /> Allergeni</Label>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={onSuggest} disabled={suggesting}>
            {suggesting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Suggerisci con AI
          </Button>
        </div>

        {dish.allergens_document?.length > 0 && (
          <p className="text-xs text-muted-foreground mb-2">Dal documento: {dish.allergens_document.map(label).join(", ")}</p>
        )}

        {suggested.length > 0 && (
          <div className="mb-3 space-y-1.5">
            <p className="text-xs font-semibold text-primary">Suggeriti dall'AI (da confermare):</p>
            {suggested.map((s) => (
              <div key={s.code} className="flex items-start gap-2 text-xs bg-white rounded-lg px-2.5 py-1.5 border border-border">
                <Sparkles className="h-3 w-3 text-primary mt-0.5 shrink-0" />
                <span><b>{label(s.code)}</b> — {s.reason} <span className="text-muted-foreground">({s.confidence})</span></span>
              </div>
            ))}
          </div>
        )}

        {needsInfo.length > 0 && (
          <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
            <p className="font-semibold flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> L'AI chiede maggiori dettagli:</p>
            <ul className="list-disc ml-4 mt-1 space-y-0.5">{needsInfo.map((q, i) => <li key={i}>{q}</li>)}</ul>
          </div>
        )}

        <p className="text-xs text-muted-foreground mb-2">Conferma gli allergeni presenti (14 categorie UE):</p>
        <div className="grid grid-cols-2 gap-1.5">
          {catalog.map((a) => {
            const checked = dish.allergens_confirmed.includes(a.code);
            const sugg = suggested.find((s) => s.code === a.code);
            return (
              <label key={a.code} className={`flex items-center gap-2 rounded-lg px-2 py-1.5 cursor-pointer text-xs ${checked ? "bg-emerald-50" : "hover:bg-white"} ${sugg && !checked ? "ring-1 ring-primary/30" : ""}`}>
                <Checkbox checked={checked} onCheckedChange={() => toggleConfirmed(a.code)} />
                <span>{a.label}</span>
              </label>
            );
          })}
        </div>

        <div className="mt-3 flex items-center justify-between">
          {dish.allergen_status === "confermato" ? (
            <Badge className="bg-emerald-50 text-emerald-600 gap-1 text-xs"><ShieldCheck className="h-3 w-3" /> Confermato dal gestore</Badge>
          ) : dish.allergen_status === "da_aggiornare" ? (
            <Badge variant="outline" className="text-amber-600 text-xs">Ingredienti cambiati — riconferma</Badge>
          ) : (
            <Badge variant="outline" className="text-muted-foreground text-xs">In bozza, da confermare</Badge>
          )}
          <Button size="sm" className="btn-grad text-white font-semibold" onClick={confirmAll} data-testid="confirm-allergens">Conferma allergeni</Button>
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">I suggerimenti AI restano proposte finché non li confermi. Le possibili contaminazioni vanno verificate in base ai tuoi prodotti e procedure.</p>
      </div>

      <label className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
        <span className="text-sm">Disponibile</span>
        <Switch checked={dish.available} onCheckedChange={(v) => set("available", v)} />
      </label>
    </div>
  );
}
